import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  Badge,
  CircularProgress,
  Button,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import MessageIcon from '@mui/icons-material/Message';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorIcon from '@mui/icons-material/Error';
import ReplyIcon from '@mui/icons-material/Reply';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddIcon from '@mui/icons-material/Add';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EventIcon from '@mui/icons-material/Event';
import DescriptionIcon from '@mui/icons-material/Description';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import SaveIcon from '@mui/icons-material/Save';
import MarkChatUnreadIcon from '@mui/icons-material/MarkChatUnread';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../services/api';
import { Conversation, Message } from '../types/Message';
import MediaMessage from '../components/MediaMessage';
import ActivityForm from '../components/ActivityForm';
import LeadEditModal from '../components/LeadEditModal';
import { useAuth } from '../contexts/AuthContext';

// Interfaces para sidebar
interface Stage {
  id: number;
  name: string;
  color: string;
  display_order: number;
  is_won_stage: boolean;
  is_lost_stage: boolean;
  probability: number | null;
}

interface LeadTag {
  id: number;
  name: string;
  color: string;
}

interface LeadNote {
  id: number;
  content: string;
  created_by_name: string | null;
  created_at: string;
  is_private: boolean;
}

interface LeadInteraction {
  id: number;
  interaction_type: string;
  interaction_type_display: string;
  subject: string | null;
  notes: string;
  direction: string;
  created_at: string;
  created_by_name: string | null;
}

interface Lead {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  status: string;
  status_display?: string;
  source: string | null;
  source_display?: string;
  priority?: string;
  priority_display?: string;
  notes: string | null;
  probability?: number | null;
  tags?: LeadTag[];
  // Histórico comercial
  compras_count?: number;
  ticket_medio?: number | null;
  gasto_total?: number | null;
  ultima_compra?: string | null;
  // Datas
  created_at?: string;
  first_contact_date?: string | null;
  last_contact_date?: string | null;
  follow_up_date?: string | null;
}

interface Opportunity {
  id: number;
  name?: string;
  lead: Lead;
  pipeline: number | { id: number; name: string };
  pipeline_name: string;
  stage: Stage;
  stage_name: string;
  status: string;
  final_value: number;
}

interface Pipeline {
  id: number;
  name: string;
  color: string;
  is_default?: boolean;
  stages: Stage[];
}

interface AIAgent {
  id: number;
  name: string;
  type: string;
  is_active: boolean;
}

interface Task {
  id: number;
  title: string;
  description: string;
  activity_type: string;
  activity_type_display: string;
  status: string;
  status_display: string;
  priority: string;
  priority_display: string;
  scheduled_at: string | null;
  completed_at: string | null;
  lead: number | null;
  lead_name: string | null;
  deal: number | null;
  deal_name: string | null;
}

interface ConversationAgentInfo {
  id: number;
  agent: number;
  agent_name: string;
  is_active: boolean;
  auto_respond: boolean;
  is_human_handling: boolean;
  handoff_by: number | null;
  handoff_by_name: string | null;
  handoff_at: string | null;
  handoff_reason: string | null;
  handoff_reason_display: string | null;
  handoff_notes: string | null;
}

// Constantes
const MESSAGES_PER_PAGE = 30;

// Glassmorphism styles
const glassStyle = {
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
};

const Messaging = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<number | null>(null);
  const [pendingContactId, setPendingContactId] = useState<number | null>(null);
  const [selectedConversationData, setSelectedConversationData] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [displayedMessages, setDisplayedMessages] = useState<Message[]>([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState<string>('all');
  const filterUserRef = useRef<string>('all');
  const [messageText, setMessageText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [attachedPreviewUrl, setAttachedPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sidebar states
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState<number | null>(null);
  const [lead, setLead] = useState<Lead | null>(null);
  const [loadingSidebar, setLoadingSidebar] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [savingStage, setSavingStage] = useState(false);
  const [savingPipeline, setSavingPipeline] = useState(false);
  const [editingLead, setEditingLead] = useState(false);
  const [leadEditModalOpen, setLeadEditModalOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: ''
  });
  const [expandedSection, setExpandedSection] = useState<string | false>(false);

  // Deal custom fields states
  const [dealCustomFieldDefs, setDealCustomFieldDefs] = useState<any[]>([]);
  const [dealCustomFieldValues, setDealCustomFieldValues] = useState<Record<string, any>>({});
  const [savingDealFields, setSavingDealFields] = useState(false);

  // Atendente states
  const [assigningUser, setAssigningUser] = useState(false);

  // AI Agent states
  const [conversationAgent, setConversationAgent] = useState<ConversationAgentInfo | null>(null);
  const [availableAgents, setAvailableAgents] = useState<AIAgent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [loadingAgent, setLoadingAgent] = useState(false);
  const [togglingAgent, setTogglingAgent] = useState(false);
  const [handoffModalOpen, setHandoffModalOpen] = useState(false);
  const [handoffReason, setHandoffReason] = useState('manual');
  const [handoffNotes, setHandoffNotes] = useState('');

  // Task states
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<any | undefined>(undefined);
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);

  // Enhanced Lead Info states
  const [availableTags, setAvailableTags] = useState<LeadTag[]>([]);
  const [leadNotes, setLeadNotes] = useState<LeadNote[]>([]);
  const [leadInteractions, setLeadInteractions] = useState<LeadInteraction[]>([]);
  const [loadingLeadInfo, setLoadingLeadInfo] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [showTagSelector, setShowTagSelector] = useState(false);
  const [savingTag, setSavingTag] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);
  const [showCreateTag, setShowCreateTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#ec4899');
  const [creatingTag, setCreatingTag] = useState(false);

  // Buscar usuários da empresa para o dropdown de responsável
  useEffect(() => {
    const fetchCompanyUsers = async () => {
      try {
        const response = await api.get('/auth/users/company_users/');
        const data = response.data;
        const usersData = Array.isArray(data) ? data : (data.results || []);
        const mappedUsers = usersData.map((u: any) => ({
          id: u.id,
          email: u.email,
          get_full_name: u.get_full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
          role: u.role
        }));
        setCompanyUsers(mappedUsers);
      } catch (error) {
        console.error('Erro ao carregar usuários:', error);
        setCompanyUsers([]);
      }
    };
    fetchCompanyUsers();
  }, []);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  // Handle query params for deep linking (conversation, contact, or lead)
  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    const contactId = searchParams.get('contact');
    const leadId = searchParams.get('lead');

    if (conversationId) {
      // Direct conversation ID - select it
      setSelectedConversation(parseInt(conversationId));
      // Clear params after using
      setSearchParams({});
    } else if (contactId) {
      // Contact ID - need to find conversation for this contact
      setPendingContactId(parseInt(contactId));
    } else if (leadId) {
      // Lead ID - need to find WhatsApp contact for this lead, then conversation
      findConversationByLead(parseInt(leadId));
    }
  }, [searchParams]);

  // When conversations load and we have a pending contact, find and select the conversation
  useEffect(() => {
    if (pendingContactId && conversations.length > 0) {
      const conv = conversations.find(c => c.contact === pendingContactId);
      if (conv) {
        setSelectedConversation(conv.id);
        setPendingContactId(null);
        setSearchParams({});
      } else {
        // No conversation found for this contact - could create one or show message
        console.log('No conversation found for contact:', pendingContactId);
        setPendingContactId(null);
        setSearchParams({});
      }
    }
  }, [conversations, pendingContactId]);

  // Find conversation by lead ID (looks for WhatsApp contact first)
  const findConversationByLead = async (leadId: number) => {
    try {
      // First, get contacts for this lead
      const response = await api.get(`/leads/leads/${leadId}/channels/`);
      const channels = response.data.channels || [];

      // Prefer WhatsApp channel
      const whatsappChannel = channels.find((ch: any) => ch.channel === 'whatsapp');
      const contactToUse = whatsappChannel || channels[0];

      if (contactToUse) {
        setPendingContactId(contactToUse.id);
      } else {
        console.log('No contact found for lead:', leadId);
        setSearchParams({});
      }
    } catch (error) {
      console.error('Error finding conversation by lead:', error);
      setSearchParams({});
    }
  };

  // Sync filterUser para ref (evita stale closure no polling)
  useEffect(() => {
    filterUserRef.current = filterUser;
    loadConversations();
  }, [filterUser]);

  // Polling: Auto-refresh conversations every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadConversations(true);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Load messages when conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation);
    }
  }, [selectedConversation]);

  // Polling: Auto-refresh messages every 3 seconds
  useEffect(() => {
    if (!selectedConversation) return;
    const interval = setInterval(() => {
      loadMessages(selectedConversation, true);
    }, 3000);
    return () => clearInterval(interval);
  }, [selectedConversation]);

  // Update selected conversation data
  useEffect(() => {
    if (selectedConversation) {
      const conv = conversations.find(c => c.id === selectedConversation);
      setSelectedConversationData(conv || null);
    }
  }, [selectedConversation, conversations]);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = useCallback((instant = false) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: instant ? 'instant' : 'smooth' });
    }
  }, []);

  // Track if this is the initial load
  const isInitialLoadRef = useRef(true);
  const prevMessagesCountRef = useRef(0);

  useEffect(() => {
    if (displayedMessages.length > 0) {
      // Scroll instantly on initial load, smoothly on updates
      if (isInitialLoadRef.current) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => scrollToBottom(true), 50);
        isInitialLoadRef.current = false;
      } else if (displayedMessages.length > prevMessagesCountRef.current) {
        // New messages arrived, scroll smoothly
        scrollToBottom(false);
      }
      prevMessagesCountRef.current = displayedMessages.length;
    }
  }, [displayedMessages, scrollToBottom]);

  // Reset initial load flag when conversation changes
  useEffect(() => {
    isInitialLoadRef.current = true;
    prevMessagesCountRef.current = 0;
  }, [selectedConversation]);

  const loadConversations = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const fu = filterUserRef.current;
      const params = new URLSearchParams({ page_size: '9999' });
      if (fu && fu !== 'all') params.set('atendente', fu);
      const response = await api.get(`/messaging/conversations/?${params.toString()}`);
      const data = response.data.results || response.data;

      // Only update if data actually changed (prevents flickering)
      setConversations(prev => {
        // Quick comparison by stringifying
        const prevJson = JSON.stringify(prev.map(c => ({
          id: c.id,
          unread_count: c.unread_count,
          last_message_at: c.last_message_at,
          last_message_preview: c.last_message_preview
        })));
        const newJson = JSON.stringify(data.map((c: Conversation) => ({
          id: c.id,
          unread_count: c.unread_count,
          last_message_at: c.last_message_at,
          last_message_preview: c.last_message_preview
        })));
        return prevJson === newJson ? prev : data;
      });
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (conversationId: number, silent = false) => {
    try {
      if (!silent) setLoadingMessages(true);
      const response = await api.get(`/messaging/conversations/${conversationId}/messages/`);
      const data = response.data.results || response.data;

      // Only update if messages actually changed (prevents flickering)
      setMessages(prev => {
        const prevIds = prev.map(m => `${m.id}-${m.status}`).join(',');
        const newIds = data.map((m: Message) => `${m.id}-${m.status}`).join(',');
        if (prevIds === newIds) return prev;

        // Show only last 30 messages initially
        const lastMessages = data.slice(-MESSAGES_PER_PAGE);
        setDisplayedMessages(lastMessages);
        setHasMoreMessages(data.length > MESSAGES_PER_PAGE);
        return data;
      });

      if (!silent) {
        markAsRead(conversationId);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, []);

  const loadMoreMessages = () => {
    setLoadingMore(true);
    const currentCount = displayedMessages.length;
    const newCount = currentCount + MESSAGES_PER_PAGE;
    const newMessages = messages.slice(-newCount);
    setDisplayedMessages(newMessages);
    setHasMoreMessages(messages.length > newCount);
    setLoadingMore(false);
  };

  const markAsRead = async (conversationId: number) => {
    try {
      await api.post(`/messaging/conversations/${conversationId}/mark_as_read/`);
      setConversations(prev => prev.map(c =>
        c.id === conversationId ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAsUnread = async (conversationId: number) => {
    try {
      await api.post(`/messaging/conversations/${conversationId}/mark_as_unread/`);
      setConversations(prev => prev.map(c =>
        c.id === conversationId ? { ...c, unread_count: Math.max(c.unread_count, 1) } : c
      ));
    } catch (error) {
      console.error('Error marking as unread:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() && !attachedFile) return;
    if (!selectedConversation) return;

    const textToSend = messageText.trim();
    const fileToSend = attachedFile;
    const tempId = Date.now();

    // Limpar input imediatamente
    setMessageText('');
    setAttachedFile(null);
    setAttachedPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setReplyTo(null);
    setError(null);

    // Adicionar mensagem otimisticamente
    const optimisticMessage: Message = {
      id: tempId,
      conversation: selectedConversation,
      conversation_channel: selectedConversationData?.channel || 'whatsapp',
      contact: selectedConversationData?.contact || 0,
      sender: user?.id || null,
      sender_name: user ? `${user.first_name} ${user.last_name}`.trim() || 'Você' : 'Você',
      direction: 'outbound',
      content: textToSend || (fileToSend ? `[${fileToSend.name}]` : ''),
      status: 'sending',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      media_type: null,
      media_url: null,
      media_file: null,
      thumbnail: null,
      external_id: null,
      reply_to: replyTo,
      sent_at: null,
      delivered_at: null,
      read_at: null,
      error_message: null,
      is_media: fileToSend != null,
    };

    setDisplayedMessages(prev => [...prev, optimisticMessage]);
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      const payload: any = {
        content: textToSend || '',
        conversation: selectedConversation,
      };

      if (replyTo) payload.reply_to = replyTo;

      // Se há arquivo, converter para base64 e incluir no payload
      if (fileToSend) {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]); // remover prefixo "data:...;base64,"
          };
          reader.onerror = reject;
          reader.readAsDataURL(fileToSend);
        });

        let mediaType = 'document';
        if (fileToSend.type.startsWith('image/')) mediaType = 'image';
        else if (fileToSend.type.startsWith('video/')) mediaType = 'video';
        else if (fileToSend.type.startsWith('audio/')) mediaType = 'audio';

        payload.media_base64 = base64;
        payload.media_type = mediaType;
        payload.media_filename = fileToSend.name;
        payload.media_mimetype = fileToSend.type || 'application/octet-stream';
      }

      api.post('/messaging/messages/send/', payload).then(() => {
        loadMessages(selectedConversation, true);
        loadConversations(true);
      }).catch((error: any) => {
        console.error('Error sending message:', error);
        setDisplayedMessages(prev =>
          prev.map(m => m.id === tempId ? { ...m, status: 'failed' } : m)
        );
        setError(error.response?.data?.error || error.response?.data?.detail || 'Erro ao enviar mensagem');
      });

    } catch (error: any) {
      console.error('Error sending message:', error);
      setError('Erro ao processar arquivo');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setError('Arquivo muito grande. Máximo 15MB.');
      return;
    }
    setAttachedFile(file);
    if (file.type.startsWith('image/')) {
      setAttachedPreviewUrl(URL.createObjectURL(file));
    } else {
      setAttachedPreviewUrl(null);
    }
  };

  const handleRemoveAttachment = () => {
    setAttachedFile(null);
    setAttachedPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBackToList = () => {
    setSelectedConversation(null);
    setSelectedConversationData(null);
    setMessages([]);
    setDisplayedMessages([]);
  };

  // ============ SIDEBAR FUNCTIONS ============

  // Load sidebar data when conversation changes
  useEffect(() => {
    if (selectedConversationData?.lead) {
      loadSidebarData(selectedConversationData.lead);
    } else {
      setOpportunity(null);
      setLead(null);
      setStages([]);
    }
  }, [selectedConversationData]);

  // Sync deal custom field values when opportunity changes
  useEffect(() => {
    if (opportunity) {
      setDealCustomFieldValues((opportunity as any).custom_fields || {});
    }
  }, [opportunity]);

  // Initialize lead form when lead changes
  useEffect(() => {
    if (lead) {
      setLeadForm({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        company: lead.company || '',
        notes: lead.notes || ''
      });
    }
  }, [lead]);

  const loadSidebarData = async (leadId: number) => {
    setLoadingSidebar(true);
    try {
      // Load lead data and pipelines in parallel
      const [leadResponse, pipelinesResponse, oppsResponse] = await Promise.all([
        api.get(`/leads/leads/${leadId}/`),
        api.get('/pipelines/pipelines/'),
        api.get(`/deals/deals/?lead=${leadId}`)
      ]);

      setLead(leadResponse.data);

      // Set pipelines
      const pipelinesData = pipelinesResponse.data.results || pipelinesResponse.data;
      setPipelines(pipelinesData);

      // Try to find opportunity for this lead
      const opps = oppsResponse.data.results || oppsResponse.data;

      // Load deal custom field definitions once
      if (dealCustomFieldDefs.length === 0) {
        loadDealCustomFieldDefs();
      }

      if (opps.length > 0) {
        const opp = opps[0]; // Get first opportunity
        setOpportunity(opp);
        setDealCustomFieldValues((opp as any).custom_fields || {});

        // Pipeline pode vir como objeto {id, name} ou como número
        const pipelineId = typeof opp.pipeline === 'object' ? opp.pipeline.id : opp.pipeline;
        setSelectedPipelineId(pipelineId);

        // Load pipeline stages
        if (pipelineId) {
          const pipelineResponse = await api.get(`/pipelines/pipelines/${pipelineId}/`);
          const pipelineStages = pipelineResponse.data.stages || [];
          setStages(pipelineStages.sort((a: Stage, b: Stage) => a.display_order - b.display_order));
        }
      } else {
        setOpportunity(null);
        setStages([]);
        // Set default pipeline if available
        if (pipelinesData.length > 0) {
          const defaultPipeline = pipelinesData.find((p: Pipeline) => p.is_default) || pipelinesData[0];
          setSelectedPipelineId(defaultPipeline.id);
          setStages(defaultPipeline.stages?.sort((a: Stage, b: Stage) => a.display_order - b.display_order) || []);
        }
      }
    } catch (error) {
      console.error('Error loading sidebar data:', error);
    } finally {
      setLoadingSidebar(false);
    }
  };

  const handleChangePipeline = async (pipelineId: number) => {
    if (!lead) return;
    setSavingPipeline(true);
    try {
      // Find the new pipeline
      const newPipeline = pipelines.find(p => p.id === pipelineId);
      if (!newPipeline) return;

      // Load pipeline stages
      const pipelineResponse = await api.get(`/pipelines/pipelines/${pipelineId}/`);
      const pipelineStages = pipelineResponse.data.stages || [];
      const sortedStages = pipelineStages.sort((a: Stage, b: Stage) => a.display_order - b.display_order);
      setStages(sortedStages);
      setSelectedPipelineId(pipelineId);

      // If opportunity exists, update its pipeline
      if (opportunity) {
        // Find entry stage of new pipeline
        const entryStage = sortedStages.find((s: Stage) => !s.is_won_stage && !s.is_lost_stage);
        if (entryStage) {
          await api.patch(`/deals/deals/${opportunity.id}/`, {
            pipeline: pipelineId,
            stage: entryStage.id
          });
          const response = await api.get(`/deals/deals/${opportunity.id}/`);
          setOpportunity(response.data);
        }
      }
    } catch (error) {
      console.error('Error changing pipeline:', error);
    } finally {
      setSavingPipeline(false);
    }
  };

  const handleCreateOpportunity = async (stageId: number) => {
    if (!lead || !selectedPipelineId) return;
    setSavingStage(true);
    try {
      const response = await api.post('/deals/deals/', {
        name: `Negociação - ${lead.name}`,
        lead: lead.id,
        pipeline: selectedPipelineId,
        stage: stageId,
        value: 0
      });
      setOpportunity(response.data);
    } catch (error) {
      console.error('Error creating opportunity:', error);
    } finally {
      setSavingStage(false);
    }
  };

  const handleChangeStage = async (stageId: number) => {
    if (!opportunity) {
      // Create new opportunity if none exists
      await handleCreateOpportunity(stageId);
      return;
    }
    if (stageId === opportunity.stage.id) return;
    setSavingStage(true);
    try {
      await api.post(`/deals/deals/${opportunity.id}/change-stage/`, { stage_id: stageId });
      // Reload opportunity data
      const response = await api.get(`/deals/deals/${opportunity.id}/`);
      setOpportunity(response.data);
    } catch (error) {
      console.error('Error changing stage:', error);
    } finally {
      setSavingStage(false);
    }
  };

  const handleMarkAsWon = async () => {
    if (!opportunity) return;
    setSavingStage(true);
    try {
      await api.post(`/deals/deals/${opportunity.id}/mark_won/`);
      const response = await api.get(`/deals/deals/${opportunity.id}/`);
      setOpportunity(response.data);
    } catch (error) {
      console.error('Error marking as won:', error);
    } finally {
      setSavingStage(false);
    }
  };

  const handleMarkAsLost = async (reason?: string) => {
    if (!opportunity) return;
    setSavingStage(true);
    try {
      await api.post(`/deals/deals/${opportunity.id}/mark_lost/`, {
        loss_reason: reason || 'Perdido via mensagens'
      });
      const response = await api.get(`/deals/deals/${opportunity.id}/`);
      setOpportunity(response.data);
    } catch (error) {
      console.error('Error marking as lost:', error);
    } finally {
      setSavingStage(false);
    }
  };

  const handleSaveLead = async () => {
    if (!lead) return;
    setSavingLead(true);
    try {
      await api.patch(`/leads/leads/${lead.id}/`, leadForm);
      const response = await api.get(`/leads/leads/${lead.id}/`);
      setLead(response.data);
      setEditingLead(false);
    } catch (error) {
      console.error('Error saving lead:', error);
    } finally {
      setSavingLead(false);
    }
  };

  const handleLeadUpdated = (updatedLead: Lead) => {
    setLead(updatedLead);
    // Update lead form with new data
    setLeadForm({
      name: updatedLead.name || '',
      email: updatedLead.email || '',
      phone: updatedLead.phone || '',
      company: updatedLead.company || '',
      notes: updatedLead.notes || ''
    });
  };

  // ============ TASK FUNCTIONS ============

  const loadTasks = async (leadId: number) => {
    setLoadingTasks(true);
    try {
      const response = await api.get(`/activities/activities/by_lead/?lead_id=${leadId}`);
      const data = response.data.results || response.data;
      setTasks(data);
    } catch (error) {
      console.error('Error loading tasks:', error);
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleSaveTask = async (data: any) => {
    if (!lead) return;
    setSavingTask(true);
    try {
      await api.post('/activities/activities/', {
        ...data,
        lead: lead.id,
        deal: opportunity?.id || null
      });
      setTaskModalOpen(false);
      setEditingTask(undefined);
      await loadTasks(lead.id);
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Erro ao salvar tarefa');
    } finally {
      setSavingTask(false);
    }
  };

  const handleToggleTaskStatus = async (taskId: number, currentStatus: string) => {
    try {
      if (currentStatus === 'completed') {
        // Reopen task
        await api.patch(`/activities/activities/${taskId}/`, { status: 'scheduled' });
      } else {
        // Complete task
        await api.post(`/activities/activities/${taskId}/complete/`, { outcome: 'Concluída' });
      }
      if (lead) {
        await loadTasks(lead.id);
      }
    } catch (error) {
      console.error('Error toggling task status:', error);
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta tarefa?')) return;
    try {
      await api.delete(`/activities/activities/${taskId}/`);
      if (lead) {
        await loadTasks(lead.id);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  // Load tasks when lead changes
  useEffect(() => {
    if (lead) {
      loadTasks(lead.id);
      loadLeadExtendedInfo(lead.id);
    } else {
      setTasks([]);
      setLeadNotes([]);
      setLeadInteractions([]);
    }
  }, [lead]);

  // Load available tags on mount
  useEffect(() => {
    loadAvailableTags();
  }, []);

  const loadAvailableTags = async () => {
    try {
      const response = await api.get('/leads/tags/');
      const data = response.data.results || response.data;
      setAvailableTags(data);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const loadLeadExtendedInfo = async (leadId: number) => {
    setLoadingLeadInfo(true);
    try {
      // Load notes and interactions in parallel
      const [notesResponse, interactionsResponse] = await Promise.all([
        api.get(`/leads/notes/?lead=${leadId}`),
        api.get(`/leads/interactions/?lead=${leadId}`)
      ]);

      const notesData = notesResponse.data.results || notesResponse.data;
      const interactionsData = interactionsResponse.data.results || interactionsResponse.data;

      setLeadNotes(notesData.slice(0, 5)); // Show last 5 notes
      setLeadInteractions(interactionsData.slice(0, 5)); // Show last 5 interactions
    } catch (error) {
      console.error('Error loading lead extended info:', error);
    } finally {
      setLoadingLeadInfo(false);
    }
  };

  const handleAddNote = async () => {
    if (!lead || !newNote.trim()) return;
    setSavingNote(true);
    try {
      await api.post('/leads/notes/', {
        lead: lead.id,
        content: newNote.trim()
      });
      setNewNote('');
      await loadLeadExtendedInfo(lead.id);
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!lead) return;
    setDeletingNoteId(noteId);
    try {
      await api.delete(`/leads/notes/${noteId}/`);
      await loadLeadExtendedInfo(lead.id);
    } catch (error) {
      console.error('Error deleting note:', error);
    } finally {
      setDeletingNoteId(null);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    setCreatingTag(true);
    try {
      const response = await api.post('/leads/tags/', {
        name: newTagName.trim(),
        color: newTagColor
      });
      // Add the new tag to availableTags
      setAvailableTags([...availableTags, response.data]);
      // If we have a lead, add the tag to it
      if (lead) {
        const currentTagIds = lead.tags?.map(t => t.id) || [];
        await api.patch(`/leads/leads/${lead.id}/`, {
          tags: [...currentTagIds, response.data.id]
        });
        const leadResponse = await api.get(`/leads/leads/${lead.id}/`);
        setLead(leadResponse.data);
      }
      setNewTagName('');
      setNewTagColor('#ec4899'); // Reset color to default
      setShowCreateTag(false);
      // Keep the tag selector open to show the result
    } catch (error) {
      console.error('Error creating tag:', error);
    } finally {
      setCreatingTag(false);
    }
  };

  const handleAddTag = async (tagId: number) => {
    if (!lead) return;
    setSavingTag(true);
    try {
      // Get current tag IDs and add new one
      const currentTagIds = lead.tags?.map(t => t.id) || [];
      if (!currentTagIds.includes(tagId)) {
        await api.patch(`/leads/leads/${lead.id}/`, {
          tags: [...currentTagIds, tagId]
        });
        // Reload lead data
        const response = await api.get(`/leads/leads/${lead.id}/`);
        setLead(response.data);
      }
      // Don't close the selector - let user add more tags if needed
    } catch (error) {
      console.error('Error adding tag:', error);
    } finally {
      setSavingTag(false);
    }
  };

  const handleRemoveTag = async (tagId: number) => {
    if (!lead) return;
    setSavingTag(true);
    try {
      const currentTagIds = lead.tags?.map(t => t.id) || [];
      const newTagIds = currentTagIds.filter(id => id !== tagId);
      await api.patch(`/leads/leads/${lead.id}/`, {
        tags: newTagIds
      });
      // Reload lead data
      const response = await api.get(`/leads/leads/${lead.id}/`);
      setLead(response.data);
    } catch (error) {
      console.error('Error removing tag:', error);
    } finally {
      setSavingTag(false);
    }
  };

  const handleQuickAction = (actionType: string) => {
    // Open task modal with preset values
    const presets: { [key: string]: any } = {
      'follow-up': {
        title: `Follow-up com ${lead?.name}`,
        activity_type: 'follow_up',
        priority: 'high'
      },
      'proposal': {
        title: `Enviar proposta para ${lead?.name}`,
        activity_type: 'proposal',
        priority: 'high'
      },
      'call': {
        title: `Ligar para ${lead?.name}`,
        activity_type: 'call',
        priority: 'medium'
      }
    };
    setEditingTask(presets[actionType] || undefined);
    setTaskModalOpen(true);
  };

  // Helper function to get lead temperature/score
  const getLeadTemperature = (lead: Lead) => {
    const probability = lead.probability || 0;
    if (probability >= 70) return { label: 'Quente', color: '#ef4444', icon: WhatshotIcon, bg: 'rgba(239,68,68,0.15)' };
    if (probability >= 40) return { label: 'Morno', color: '#f59e0b', icon: WbSunnyIcon, bg: 'rgba(245,158,11,0.15)' };
    return { label: 'Frio', color: '#3b82f6', icon: AcUnitIcon, bg: 'rgba(59,130,246,0.15)' };
  };

  // Helper to format time ago
  const formatTimeAgo = (dateString: string | null | undefined) => {
    if (!dateString) return 'Nunca';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ganho': return '#22c55e';
      case 'perdido': return '#ef4444';
      default: return '#6366f1';
    }
  };

  // ============ AI AGENT FUNCTIONS ============

  // Load agent data when conversation changes
  useEffect(() => {
    if (selectedConversation) {
      loadConversationAgent();
      loadAvailableAgents();
    } else {
      setConversationAgent(null);
    }
  }, [selectedConversation]);

  const loadConversationAgent = async () => {
    if (!selectedConversation) return;
    setLoadingAgent(true);
    try {
      const response = await api.get(`/ai/conversations/?conversation_id=${selectedConversation}`);
      const data = response.data.results || response.data;
      if (data.length > 0) {
        setConversationAgent(data[0]);
      } else {
        setConversationAgent(null);
      }
    } catch (error) {
      console.error('Error loading conversation agent:', error);
      setConversationAgent(null);
    } finally {
      setLoadingAgent(false);
    }
  };

  const loadAvailableAgents = async () => {
    try {
      const response = await api.get('/ai/agents/');
      const data = response.data.results || response.data;
      setAvailableAgents(data.filter((a: AIAgent) => a.is_active));
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const handleActivateAgent = async () => {
    if (!selectedConversation || !selectedAgentId) return;
    setTogglingAgent(true);
    try {
      await api.post('/ai/conversations/start/', {
        agent_id: selectedAgentId,
        conversation_id: selectedConversation
      });
      await loadConversationAgent();
    } catch (error) {
      console.error('Error activating agent:', error);
    } finally {
      setTogglingAgent(false);
    }
  };

  const handleDeactivateAgent = async () => {
    if (!conversationAgent) return;
    setTogglingAgent(true);
    try {
      await api.post(`/ai/conversations/${conversationAgent.id}/end_conversation/`);
      await loadConversationAgent();
    } catch (error) {
      console.error('Error deactivating agent:', error);
    } finally {
      setTogglingAgent(false);
    }
  };

  const handleReactivateAgent = async () => {
    if (!conversationAgent) return;
    setTogglingAgent(true);
    try {
      // Reactivate by setting is_active to true
      await api.patch(`/ai/conversations/${conversationAgent.id}/`, {
        is_active: true,
        auto_respond: true
      });
      await loadConversationAgent();
    } catch (error) {
      console.error('Error reactivating agent:', error);
    } finally {
      setTogglingAgent(false);
    }
  };

  const handleSwitchAgent = async (newAgentId: number) => {
    if (!selectedConversation) return;
    setTogglingAgent(true);
    try {
      // First, deactivate current agent if exists
      if (conversationAgent) {
        await api.delete(`/ai/conversations/${conversationAgent.id}/`);
      }
      // Then create new agent association
      await api.post('/ai/conversations/start/', {
        agent_id: newAgentId,
        conversation_id: selectedConversation
      });
      await loadConversationAgent();
    } catch (error) {
      console.error('Error switching agent:', error);
    } finally {
      setTogglingAgent(false);
    }
  };

  const handleHandoff = async () => {
    if (!conversationAgent) return;
    setTogglingAgent(true);
    try {
      await api.post(`/ai/conversations/${conversationAgent.id}/handoff/`, {
        reason: handoffReason,
        notes: handoffNotes
      });
      setHandoffModalOpen(false);
      setHandoffReason('manual');
      setHandoffNotes('');
      await loadConversationAgent();
    } catch (error) {
      console.error('Error performing handoff:', error);
    } finally {
      setTogglingAgent(false);
    }
  };

  const handleReturnToBot = async () => {
    if (!conversationAgent) return;
    setTogglingAgent(true);
    try {
      await api.post(`/ai/conversations/${conversationAgent.id}/return_to_bot/`, {
        reactivate: true
      });
      await loadConversationAgent();
    } catch (error) {
      console.error('Error returning to bot:', error);
    } finally {
      setTogglingAgent(false);
    }
  };

  // ============ END AI AGENT FUNCTIONS ============

  // ============ DEAL CUSTOM FIELDS FUNCTIONS ============

  const loadDealCustomFieldDefs = async () => {
    try {
      const response = await api.get('/leads/custom-fields/?entity_type=deal');
      const fields = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setDealCustomFieldDefs(fields);
    } catch (error) {
      console.error('Error loading deal custom field definitions:', error);
    }
  };

  const handleDealFieldChange = (fieldName: string, value: any) => {
    setDealCustomFieldValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSaveDealCustomFields = async () => {
    if (!opportunity) return;
    setSavingDealFields(true);
    try {
      await api.patch(`/deals/deals/${opportunity.id}/`, { custom_fields: dealCustomFieldValues });
      const response = await api.get(`/deals/deals/${opportunity.id}/`);
      setOpportunity(response.data);
    } catch (error) {
      console.error('Error saving deal custom fields:', error);
    } finally {
      setSavingDealFields(false);
    }
  };

  const handleAssignUser = async (userId: number | null) => {
    if (!selectedConversation) return;
    setAssigningUser(true);
    try {
      await api.post(`/messaging/conversations/${selectedConversation}/assign/`, { user_id: userId });
      // Atualizar localmente o assigned_to na conversa selecionada
      setConversations(prev => prev.map(c => {
        if (c.id !== selectedConversation) return c;
        const user = companyUsers.find(u => u.id === userId);
        return {
          ...c,
          assigned_to: userId,
          assigned_to_id: userId,
          assigned_to_name: user?.get_full_name || user?.email || null,
          assigned_to_email: user?.email || null,
        };
      }));
    } catch (error) {
      console.error('Error assigning user:', error);
    } finally {
      setAssigningUser(false);
    }
  };

  // ============ END SIDEBAR FUNCTIONS ============

  const getChannelIcon = (channel: string) => {
    const iconProps = { sx: { fontSize: 16 } };
    switch (channel) {
      case 'whatsapp': return <WhatsAppIcon {...iconProps} />;
      case 'instagram': return <InstagramIcon {...iconProps} />;
      case 'email': return <EmailIcon {...iconProps} />;
      case 'sms': return <SmsIcon {...iconProps} />;
      default: return <MessageIcon {...iconProps} />;
    }
  };

  const getChannelColor = (channel: string) => {
    switch (channel) {
      case 'whatsapp': return '#25D366';
      case 'instagram': return '#E1306C';
      case 'email': return '#EA4335';
      case 'sms': return '#4285F4';
      default: return '#757575';
    }
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffHours < 24) {
      return format(date, 'HH:mm', { locale: ptBR });
    } else if (diffHours < 24 * 7) {
      return format(date, 'EEE', { locale: ptBR });
    } else {
      return format(date, 'dd/MM', { locale: ptBR });
    }
  };

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, 'HH:mm', { locale: ptBR });
  };

  const getStatusIcon = (status: string, direction: string) => {
    if (direction === 'inbound') return null;
    switch (status) {
      case 'read': return <CheckCircleIcon sx={{ fontSize: 12, color: '#4CAF50' }} />;
      case 'delivered': return <CheckCircleIcon sx={{ fontSize: 12, color: '#FF9800' }} />;
      case 'sent': return <CheckCircleIcon sx={{ fontSize: 12, color: '#BDBDBD' }} />;
      case 'failed': return <ErrorIcon sx={{ fontSize: 12, color: '#f44336' }} />;
      case 'queued': return <ScheduleIcon sx={{ fontSize: 12, color: '#9e9e9e' }} />;
      default: return null;
    }
  };

  // Helper para extrair preview de mensagem
  const getMessagePreview = (preview: any): string => {
    if (!preview) return 'Sem mensagens';
    if (typeof preview === 'string') return preview;
    if (typeof preview === 'object' && preview.content) return preview.content;
    return 'Sem mensagens';
  };

  // Filter conversations
  const filteredConversations = conversations.filter(conv => {
    if (!searchTerm) return !conv.is_archived;
    const term = searchTerm.toLowerCase();
    return (
      !conv.is_archived &&
      (conv.contact_name.toLowerCase().includes(term) ||
       conv.lead_name?.toLowerCase().includes(term))
    );
  }).sort((a, b) => {
    // Não lidas sempre primeiro; dentro de cada grupo, mais recente primeiro
    const aUnread = a.unread_count > 0 ? 1 : 0;
    const bUnread = b.unread_count > 0 ? 1 : 0;
    if (bUnread !== aUnread) return bUnread - aUnread;
    const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
    const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
    return bTime - aTime;
  });

  // Group messages by date
  const groupMessagesByDate = (msgs: Message[]) => {
    const groups: { [key: string]: Message[] } = {};
    msgs.forEach(msg => {
      const date = format(new Date(msg.created_at), 'dd/MM/yyyy', { locale: ptBR });
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });
    return groups;
  };

  const showList = !isMobile || !selectedConversation;
  const showChat = !isMobile || selectedConversation;

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)',
      overflow: 'hidden',
      p: 2,
      gap: 2
    }}>
      {/* Header */}
      <Box sx={{
        ...glassStyle,
        borderRadius: 3,
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <MessageIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937' }}>
              Central de Mensagens
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              {conversations.length} conversas • {conversations.filter(c => c.unread_count > 0).length} não lidas
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Atualizar">
          <IconButton
            onClick={() => loadConversations(false)}
            disabled={loading}
            sx={{
              ...glassStyle,
              '&:hover': { background: 'rgba(255,255,255,0.9)' }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', gap: 2, overflow: 'hidden' }}>
        {/* Conversations List */}
        {showList && (
          <Box sx={{
            width: isMobile ? '100%' : 360,
            minWidth: isMobile ? '100%' : 360,
            ...glassStyle,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
          }}>
            {/* Search */}
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar conversas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#9ca3af' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255,255,255,0.8)',
                    borderRadius: 2,
                    '& fieldset': { border: '1px solid rgba(0,0,0,0.05)' },
                    '&:hover fieldset': { borderColor: '#6366f1' },
                    '&.Mui-focused fieldset': { borderColor: '#6366f1' }
                  }
                }}
              />
              {companyUsers.length > 0 && (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <Select
                    value={filterUser}
                    onChange={(e) => setFilterUser(e.target.value)}
                    displayEmpty
                    sx={{
                      background: 'rgba(255,255,255,0.8)',
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': { border: '1px solid rgba(0,0,0,0.05)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
                    }}
                  >
                    <MenuItem value="all">Todos os atendentes</MenuItem>
                    {companyUsers.map((u: any) => (
                      <MenuItem key={u.id} value={String(u.id)}>
                        {u.get_full_name || u.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            {/* Conversations */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : filteredConversations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">Nenhuma conversa encontrada</Typography>
                </Box>
              ) : (
                filteredConversations.map((conv) => (
                  <Box
                    key={conv.id}
                    onClick={() => setSelectedConversation(conv.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      mb: 0.5,
                      transition: 'all 0.2s',
                      background: selectedConversation === conv.id
                        ? 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))'
                        : 'transparent',
                      border: selectedConversation === conv.id
                        ? '1px solid rgba(99,102,241,0.3)'
                        : '1px solid transparent',
                      '&:hover': {
                        background: selectedConversation === conv.id
                          ? 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))'
                          : 'rgba(255,255,255,0.8)',
                      }
                    }}
                  >
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        <Box sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: getChannelColor(conv.channel),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid #fff'
                        }}>
                          {React.cloneElement(getChannelIcon(conv.channel), {
                            sx: { fontSize: 10, color: '#fff' }
                          })}
                        </Box>
                      }
                    >
                      <Avatar
                        src={conv.contact_avatar || undefined}
                        sx={{
                          width: 48,
                          height: 48,
                          background: `linear-gradient(135deg, ${getChannelColor(conv.channel)}80, ${getChannelColor(conv.channel)})`
                        }}
                      >
                        {conv.contact_name.charAt(0).toUpperCase()}
                      </Avatar>
                    </Badge>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, color: '#1f2937' }}
                            noWrap
                          >
                            {conv.contact_name}
                          </Typography>
                          {conv.stage_name && (
                            <Chip
                              label={conv.stage_name}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                fontWeight: 500,
                                backgroundColor: conv.stage_color || '#6366f1',
                                color: 'white',
                                '& .MuiChip-label': { px: 1 }
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: '#9ca3af', ml: 1, flexShrink: 0 }}>
                          {formatTime(conv.last_message_at)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.25 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: '#6b7280', fontSize: 12 }}
                          noWrap
                        >
                          {getMessagePreview(conv.last_message_preview)}
                        </Typography>
                        {conv.unread_count > 0 && (
                          <Chip
                            label={conv.unread_count}
                            size="small"
                            sx={{
                              height: 20,
                              minWidth: 20,
                              fontSize: 11,
                              fontWeight: 700,
                              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                              color: '#fff',
                              ml: 1
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        )}

        {/* Chat Area */}
        {showChat && (
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            ...glassStyle,
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
          }}>
            {!selectedConversation ? (
              // Empty State
              <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                p: 4
              }}>
                <Box sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(99,102,241,0.2), rgba(139,92,246,0.2))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 56, color: '#6366f1' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
                  Selecione uma conversa
                </Typography>
                <Typography variant="body1" sx={{ color: '#6b7280', maxWidth: 400 }}>
                  Escolha uma conversa da lista para visualizar as mensagens
                </Typography>
              </Box>
            ) : (
              <>
                {/* Chat Header */}
                <Box sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid rgba(0,0,0,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  background: 'rgba(255,255,255,0.5)'
                }}>
                  {isMobile && (
                    <IconButton onClick={handleBackToList} size="small">
                      <ArrowBackIcon />
                    </IconButton>
                  )}
                  <Avatar
                    src={selectedConversationData?.contact_avatar || undefined}
                    sx={{
                      width: 44,
                      height: 44,
                      background: `linear-gradient(135deg, ${getChannelColor(selectedConversationData?.channel || 'whatsapp')}80, ${getChannelColor(selectedConversationData?.channel || 'whatsapp')})`
                    }}
                  >
                    {selectedConversationData?.contact_name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1f2937' }}>
                      {selectedConversationData?.contact_name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        icon={getChannelIcon(selectedConversationData?.channel || 'whatsapp')}
                        label={selectedConversationData?.channel_display_short || selectedConversationData?.channel}
                        size="small"
                        sx={{
                          height: 22,
                          background: `${getChannelColor(selectedConversationData?.channel || 'whatsapp')}15`,
                          color: getChannelColor(selectedConversationData?.channel || 'whatsapp'),
                          '& .MuiChip-icon': {
                            color: getChannelColor(selectedConversationData?.channel || 'whatsapp')
                          }
                        }}
                      />
                      {opportunity?.stage?.name && (
                        <Chip
                          label={opportunity.stage.name}
                          size="small"
                          sx={{
                            height: 22,
                            backgroundColor: opportunity.stage.color || '#6366f1',
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Marcar como não lida */}
                  <Tooltip title="Marcar como não lida">
                    <IconButton
                      size="small"
                      onClick={() => selectedConversation && markAsUnread(selectedConversation)}
                      sx={{
                        color: '#6366f1',
                        '&:hover': { background: 'rgba(99,102,241,0.08)' }
                      }}
                    >
                      <MarkChatUnreadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Toggle Lead Info Panel */}
                  {!isMobile && (
                    <Button
                      variant={sidebarOpen ? "contained" : "outlined"}
                      size="small"
                      onClick={() => setSidebarOpen(!sidebarOpen)}
                      startIcon={<PersonIcon />}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 2,
                        fontWeight: 600,
                        fontSize: 13,
                        ...(sidebarOpen ? {
                          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                          color: '#fff',
                          border: 'none',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
                          }
                        } : {
                          borderColor: '#6366f1',
                          color: '#6366f1',
                          '&:hover': {
                            borderColor: '#4f46e5',
                            background: 'rgba(99,102,241,0.08)'
                          }
                        })
                      }}
                    >
                      {sidebarOpen ? 'Fechar' : 'Detalhes do Lead'}
                    </Button>
                  )}
                </Box>

                {/* Messages Area */}
                <Box
                  ref={messagesContainerRef}
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'linear-gradient(180deg, rgba(240,244,248,0.5) 0%, rgba(226,232,240,0.5) 100%)',
                    '&::-webkit-scrollbar': {
                      width: 6,
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(0,0,0,0.1)',
                      borderRadius: 3,
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: 'rgba(0,0,0,0.2)',
                    }
                  }}
                >
                  {loadingMessages ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={32} />
                    </Box>
                  ) : displayedMessages.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="text.secondary">Nenhuma mensagem</Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Load More Button */}
                      {hasMoreMessages && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={loadingMore ? <CircularProgress size={16} /> : <KeyboardArrowUpIcon />}
                            onClick={loadMoreMessages}
                            disabled={loadingMore}
                            sx={{
                              borderRadius: 3,
                              textTransform: 'none',
                              px: 3,
                              background: 'rgba(255,255,255,0.8)',
                              backdropFilter: 'blur(10px)',
                              borderColor: 'rgba(99,102,241,0.3)',
                              color: '#6366f1',
                              '&:hover': {
                                background: 'rgba(255,255,255,0.95)',
                                borderColor: '#6366f1'
                              }
                            }}
                          >
                            Ver mensagens anteriores
                          </Button>
                        </Box>
                      )}

                      {/* Messages grouped by date */}
                      {Object.entries(groupMessagesByDate(displayedMessages)).map(([date, dateMessages]) => (
                        <Box key={date} sx={{ mb: 2 }}>
                          {/* Date Separator */}
                          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            <Chip
                              label={date}
                              size="small"
                              sx={{
                                background: 'rgba(255,255,255,0.9)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(0,0,0,0.05)',
                                fontSize: 11,
                                height: 24
                              }}
                            />
                          </Box>

                          {/* Messages */}
                          {dateMessages.map((msg) => {
                            const isOutbound = msg.direction === 'outbound';
                            return (
                              <Box
                                key={msg.id}
                                sx={{
                                  display: 'flex',
                                  justifyContent: isOutbound ? 'flex-end' : 'flex-start',
                                  mb: 1
                                }}
                              >
                                <Box
                                  sx={{
                                    maxWidth: '70%',
                                    p: 1.5,
                                    borderRadius: isOutbound ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                    background: isOutbound
                                      ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                                      : 'rgba(255,255,255,0.95)',
                                    backdropFilter: 'blur(10px)',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    border: isOutbound ? 'none' : '1px solid rgba(0,0,0,0.05)'
                                  }}
                                >
                                  {/* Sender name for inbound */}
                                  {!isOutbound && msg.sender_name && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: '#6366f1',
                                        fontWeight: 600,
                                        display: 'block',
                                        mb: 0.5
                                      }}
                                    >
                                      {msg.sender_name}
                                    </Typography>
                                  )}

                                  {/* Media Message Component */}
                                  <MediaMessage message={msg} isOutbound={isOutbound} />

                                  {/* Message content (only if not media) */}
                                  {!msg.media_url && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: isOutbound ? '#fff' : '#1f2937',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.4
                                      }}
                                    >
                                      {msg.content}
                                    </Typography>
                                  )}

                                  {/* Time and status */}
                                  <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    gap: 0.5,
                                    mt: 0.5
                                  }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: 10,
                                        color: isOutbound ? 'rgba(255,255,255,0.8)' : '#9ca3af'
                                      }}
                                    >
                                      {formatMessageTime(msg.created_at)}
                                    </Typography>
                                    {getStatusIcon(msg.status, msg.direction)}
                                    <Tooltip title="Responder">
                                      <IconButton
                                        size="small"
                                        onClick={() => setReplyTo(msg.id)}
                                        sx={{
                                          p: 0.25,
                                          ml: 0.5,
                                          color: isOutbound ? 'rgba(255,255,255,0.8)' : '#9ca3af',
                                          '&:hover': {
                                            background: isOutbound
                                              ? 'rgba(255,255,255,0.1)'
                                              : 'rgba(0,0,0,0.05)'
                                          }
                                        }}
                                      >
                                        <ReplyIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      ))}

                      {/* Scroll anchor */}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </Box>

                {/* Message Input */}
                <Box sx={{
                  p: 2,
                  borderTop: '1px solid rgba(0,0,0,0.05)',
                  background: 'rgba(255,255,255,0.5)'
                }}>
                  {/* Reply indicator */}
                  {replyTo && (
                    <Box sx={{
                      mb: 1.5,
                      p: 1,
                      borderRadius: 2,
                      background: 'rgba(99,102,241,0.1)',
                      border: '1px solid rgba(99,102,241,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Typography variant="caption" sx={{ color: '#6366f1' }}>
                        Respondendo mensagem
                      </Typography>
                      <IconButton size="small" onClick={() => setReplyTo(null)}>
                        <CancelIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}

                  {/* Error message */}
                  {error && (
                    <Box sx={{
                      mb: 1.5,
                      p: 1,
                      borderRadius: 2,
                      background: 'rgba(239,68,68,0.1)',
                      border: '1px solid rgba(239,68,68,0.2)'
                    }}>
                      <Typography variant="caption" sx={{ color: '#ef4444' }}>
                        {error}
                      </Typography>
                    </Box>
                  )}

                  {/* Preview de arquivo anexado */}
                  {attachedFile && (
                    <Box sx={{
                      mb: 1,
                      p: 1,
                      borderRadius: 2,
                      background: 'rgba(99,102,241,0.08)',
                      border: '1px solid rgba(99,102,241,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      {attachedPreviewUrl ? (
                        <img
                          src={attachedPreviewUrl}
                          alt="Preview"
                          style={{ maxHeight: 64, maxWidth: 64, borderRadius: 4, objectFit: 'cover' }}
                        />
                      ) : (
                        <AttachFileIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontSize: 13, fontWeight: 500 }}>
                          {attachedFile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          {(attachedFile.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={handleRemoveAttachment}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  {/* Input area */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 1,
                    p: 1,
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(0,0,0,0.05)'
                  }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                      accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                    />
                    <IconButton
                      size="small"
                      sx={{ color: '#6b7280' }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <AttachFileIcon />
                    </IconButton>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      placeholder="Digite sua mensagem..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      autoFocus
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: 14 }
                      }}
                    />
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={!messageText.trim() && !attachedFile}
                      sx={{
                        background: (messageText.trim() || attachedFile)
                          ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                          : 'rgba(0,0,0,0.05)',
                        color: (messageText.trim() || attachedFile) ? '#fff' : '#9ca3af',
                        '&:hover': {
                          background: (messageText.trim() || attachedFile)
                            ? 'linear-gradient(135deg, #4f46e5, #7c3aed)'
                            : 'rgba(0,0,0,0.1)'
                        },
                        '&.Mui-disabled': {
                          color: '#9ca3af'
                        }
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Right Sidebar - Lead Details (não sobrepõe o chat) */}
        {showChat && selectedConversation && sidebarOpen && !isMobile && (
          <Box sx={{
            width: 340,
            minWidth: 340,
            height: '100%',
            ...glassStyle,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
          }}>
            {/* Sidebar Header */}
            <Box sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid rgba(0,0,0,0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.04))',
              flexShrink: 0
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ fontSize: 20, color: '#6366f1' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f2937' }}>
                  Detalhes do Lead
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setSidebarOpen(false)} sx={{ color: '#6b7280' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Sidebar Content */}
            <Box sx={{
                flex: 1,
                overflow: 'auto',
                p: 0,
                '&::-webkit-scrollbar': {
                  width: 6,
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(0,0,0,0.1)',
                  borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: 'rgba(0,0,0,0.2)',
                }
              }}>
                {loadingSidebar ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : !lead ? (
                  <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                    <PersonIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Nenhum lead vinculado
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Atendente Section */}
                    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <SupportAgentIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
                            Atendente
                          </Typography>
                        </Box>
                      </Box>
                      <FormControl fullWidth size="small">
                        <Select
                          value={selectedConversationData?.assigned_to_id ?? ''}
                          onChange={(e) => handleAssignUser(e.target.value === '' ? null : Number(e.target.value))}
                          disabled={assigningUser}
                          displayEmpty
                          sx={{
                            fontSize: 13,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(99,102,241,0.25)'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(99,102,241,0.5)'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#6366f1'
                            }
                          }}
                        >
                          <MenuItem value=""><em style={{ color: '#9ca3af' }}>Sem atendente</em></MenuItem>
                          {companyUsers.map(u => (
                            <MenuItem key={u.id} value={u.id} sx={{ fontSize: 13 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 22, height: 22, fontSize: 11, bgcolor: '#6366f1' }}>
                                  {(u.get_full_name || u.email || '?').charAt(0).toUpperCase()}
                                </Avatar>
                                {u.get_full_name || u.email}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Pipeline Section */}
                    <Accordion
                      expanded={expandedSection === 'pipeline'}
                      onChange={(_, exp) => setExpandedSection(exp ? 'pipeline' : false)}
                      disableGutters
                      elevation={0}
                      sx={{
                        background: 'transparent',
                        '&:before': { display: 'none' }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          px: 2,
                          minHeight: 48,
                          '& .MuiAccordionSummary-content': { my: 0 }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingUpIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                            Negociação
                          </Typography>
                          {opportunity && (
                            <Chip
                              label={opportunity.stage_name}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: 11,
                                fontWeight: 600,
                                background: `${opportunity.stage.color}20`,
                                color: opportunity.stage.color,
                                border: `1px solid ${opportunity.stage.color}40`,
                                ml: 'auto'
                              }}
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, pt: 0 }}>
                        {/* Value Summary (if opportunity exists) */}
                        {opportunity && (
                          <Box sx={{
                            p: 2,
                            mb: 2,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #6366f115, #6366f108)',
                            border: '1px solid rgba(99, 102, 241, 0.2)'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                                Valor da Negociação
                              </Typography>
                              <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1f2937' }}>
                                {formatCurrency(opportunity.final_value)}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Pipeline Selector */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5, fontWeight: 500 }}>
                            Pipeline
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select
                              value={selectedPipelineId || ''}
                              onChange={(e) => handleChangePipeline(e.target.value as number)}
                              disabled={savingPipeline}
                              displayEmpty
                              sx={{
                                borderRadius: 2,
                                '& .MuiSelect-select': {
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }
                              }}
                              renderValue={(value) => {
                                const selected = pipelines.find(p => p.id === value);
                                if (!selected) return <Typography sx={{ color: '#9ca3af' }}>Selecione...</Typography>;
                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      background: selected.color,
                                      flexShrink: 0
                                    }} />
                                    <Typography sx={{ fontWeight: 500 }}>{selected.name}</Typography>
                                  </Box>
                                );
                              }}
                            >
                              {pipelines.map((pipeline) => (
                                <MenuItem key={pipeline.id} value={pipeline.id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      background: pipeline.color,
                                      flexShrink: 0
                                    }} />
                                    <Typography>{pipeline.name}</Typography>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>

                        {/* Stage Progress */}
                        {stages.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 1, fontWeight: 500 }}>
                              Etapa {opportunity ? '(clique para alterar)' : '(selecione para criar negociação)'}
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.5,
                              p: 1,
                              borderRadius: 2,
                              background: 'rgba(0,0,0,0.02)',
                              border: '1px solid rgba(0,0,0,0.05)'
                            }}>
                              {stages.filter(s => !s.is_won_stage && !s.is_lost_stage).map((stage) => {
                                const isCurrent = opportunity && stage.id === opportunity.stage.id;
                                const isPast = opportunity && stage.display_order < opportunity.stage.display_order;
                                return (
                                  <Box
                                    key={stage.id}
                                    onClick={() => !savingStage && !savingPipeline && handleChangeStage(stage.id)}
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 1.5,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5,
                                      cursor: (savingStage || savingPipeline) ? 'wait' : 'pointer',
                                      background: isCurrent
                                        ? `${stage.color}15`
                                        : isPast
                                          ? 'rgba(34,197,94,0.06)'
                                          : 'white',
                                      border: isCurrent
                                        ? `2px solid ${stage.color}`
                                        : '1px solid transparent',
                                      boxShadow: isCurrent ? `0 2px 8px ${stage.color}20` : 'none',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        background: isCurrent ? `${stage.color}20` : `${stage.color}10`,
                                        transform: 'translateX(2px)',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                      }
                                    }}
                                  >
                                    <Box sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      background: isCurrent ? stage.color : isPast ? '#22c55e' : '#d1d5db',
                                      flexShrink: 0,
                                      boxShadow: isCurrent ? `0 0 6px ${stage.color}60` : 'none'
                                    }} />
                                    <Typography sx={{
                                      fontSize: 13,
                                      fontWeight: isCurrent ? 600 : 500,
                                      color: isCurrent ? stage.color : isPast ? '#22c55e' : '#4b5563',
                                      flex: 1
                                    }}>
                                      {stage.name}
                                    </Typography>
                                    {isPast && <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />}
                                    {isCurrent && (
                                      <Chip
                                        label="Atual"
                                        size="small"
                                        sx={{
                                          height: 18,
                                          fontSize: 10,
                                          fontWeight: 600,
                                          background: stage.color,
                                          color: 'white'
                                        }}
                                      />
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}

                        {/* Win/Lose Buttons */}
                        {opportunity && opportunity.status === 'open' && (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              fullWidth
                              size="small"
                              variant="contained"
                              startIcon={<EmojiEventsIcon />}
                              disabled={savingStage}
                              onClick={handleMarkAsWon}
                              sx={{
                                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(34,197,94,0.3)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #16a34a, #15803d)'
                                }
                              }}
                            >
                              Ganho
                            </Button>
                            <Button
                              fullWidth
                              size="small"
                              variant="outlined"
                              startIcon={<ThumbDownIcon />}
                              disabled={savingStage}
                              onClick={() => handleMarkAsLost()}
                              sx={{
                                borderColor: '#ef4444',
                                color: '#ef4444',
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 2,
                                '&:hover': {
                                  background: 'rgba(239,68,68,0.1)',
                                  borderColor: '#dc2626'
                                }
                              }}
                            >
                              Perda
                            </Button>
                          </Box>
                        )}

                        {/* Status Badge for Won/Lost */}
                        {opportunity && opportunity.status !== 'open' && (
                          <Box sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: opportunity.status === 'ganho'
                              ? 'rgba(34,197,94,0.15)'
                              : 'rgba(239,68,68,0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            {opportunity.status === 'ganho' ? (
                              <EmojiEventsIcon sx={{ color: '#22c55e' }} />
                            ) : (
                              <ThumbDownIcon sx={{ color: '#ef4444' }} />
                            )}
                            <Typography sx={{
                              fontWeight: 700,
                              color: getStatusColor(opportunity.status)
                            }}>
                              {opportunity.status === 'ganho' ? 'Negócio Ganho!' : 'Negócio Perdido'}
                            </Typography>
                          </Box>
                        )}

                        {/* No pipeline selected message */}
                        {pipelines.length === 0 && (
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                              Nenhuma pipeline disponível
                            </Typography>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>

                    {/* Deal Custom Fields Section */}
                    {opportunity && dealCustomFieldDefs.length > 0 && (
                      <>
                        <Divider />
                        <Accordion
                          expanded={expandedSection === 'deal-fields'}
                          onChange={(_, exp) => setExpandedSection(exp ? 'deal-fields' : false)}
                          disableGutters
                          elevation={0}
                          sx={{ background: 'transparent', '&:before': { display: 'none' } }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ px: 2, minHeight: 48, '& .MuiAccordionSummary-content': { my: 0 } }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TuneIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                                Campos da Negociação
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {dealCustomFieldDefs.map((field: any) => (
                                <Box key={field.id}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', mb: 0.5, display: 'block' }}>
                                    {field.label}{field.is_required ? ' *' : ''}
                                  </Typography>

                                  {field.field_type === 'select' ? (
                                    <FormControl fullWidth size="small">
                                      <Select
                                        value={dealCustomFieldValues[field.name] || ''}
                                        onChange={(e) => handleDealFieldChange(field.name, e.target.value)}
                                        displayEmpty
                                        sx={{ background: 'rgba(255,255,255,0.8)', fontSize: 13 }}
                                      >
                                        <MenuItem value=""><em>Selecionar...</em></MenuItem>
                                        {(field.options || []).map((opt: string) => (
                                          <MenuItem key={opt} value={opt} sx={{ fontSize: 13 }}>{opt}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  ) : field.field_type === 'multiselect' ? (
                                    <FormControl fullWidth size="small">
                                      <Select
                                        multiple
                                        value={Array.isArray(dealCustomFieldValues[field.name]) ? dealCustomFieldValues[field.name] : []}
                                        onChange={(e) => handleDealFieldChange(field.name, e.target.value)}
                                        renderValue={(sel: any) => (sel as string[]).join(', ')}
                                        sx={{ background: 'rgba(255,255,255,0.8)', fontSize: 13 }}
                                      >
                                        {(field.options || []).map((opt: string) => (
                                          <MenuItem key={opt} value={opt} sx={{ fontSize: 13 }}>{opt}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  ) : field.field_type === 'checkbox' ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <input
                                        type="checkbox"
                                        checked={!!dealCustomFieldValues[field.name]}
                                        onChange={(e) => handleDealFieldChange(field.name, e.target.checked)}
                                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                                      />
                                      <Typography variant="body2" sx={{ fontSize: 13 }}>{field.label}</Typography>
                                    </Box>
                                  ) : (
                                    <TextField
                                      fullWidth
                                      size="small"
                                      type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                                      value={dealCustomFieldValues[field.name] || ''}
                                      onChange={(e) => handleDealFieldChange(field.name, e.target.value)}
                                      placeholder={field.default_value || ''}
                                      InputLabelProps={field.field_type === 'date' ? { shrink: true } : undefined}
                                      sx={{
                                        '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.8)', fontSize: 13 },
                                        '& input': { fontSize: 13 }
                                      }}
                                    />
                                  )}
                                </Box>
                              ))}

                              <Button
                                fullWidth
                                size="small"
                                variant="contained"
                                onClick={handleSaveDealCustomFields}
                                disabled={savingDealFields}
                                startIcon={savingDealFields ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                                sx={{
                                  mt: 1,
                                  background: '#6366f1',
                                  fontSize: 13,
                                  '&:hover': { background: '#4f46e5' }
                                }}
                              >
                                {savingDealFields ? 'Salvando...' : 'Salvar'}
                              </Button>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      </>
                    )}

                    <Divider />

                    {/* Lead Info Section */}
                    <Accordion
                      expanded={expandedSection === 'lead'}
                      onChange={(_, exp) => setExpandedSection(exp ? 'lead' : false)}
                      disableGutters
                      elevation={0}
                      sx={{
                        background: 'transparent',
                        '&:before': { display: 'none' }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          px: 2,
                          minHeight: 48,
                          '& .MuiAccordionSummary-content': { my: 0 }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                            Dados do Lead
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, pt: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                          <Button
                            size="small"
                            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setLeadEditModalOpen(true)}
                            sx={{
                              textTransform: 'none',
                              fontSize: 12,
                              color: '#6366f1',
                              '&:hover': { background: 'rgba(99,102,241,0.1)' }
                            }}
                          >
                            Editar
                          </Button>
                        </Box>

                        {/* Display Lead Info (read-only) */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <PersonIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#9ca3af' }}>Nome</Typography>
                              <Typography sx={{ fontSize: 14, color: '#1f2937', fontWeight: 500 }}>
                                {lead.name || '-'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <EmailIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#9ca3af' }}>Email</Typography>
                              <Typography sx={{ fontSize: 14, color: '#1f2937', fontWeight: 500 }}>
                                {lead.email || '-'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <PhoneIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#9ca3af' }}>Telefone</Typography>
                              <Typography sx={{ fontSize: 14, color: '#1f2937', fontWeight: 500 }}>
                                {lead.phone || '-'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <BusinessIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#9ca3af' }}>Empresa</Typography>
                              <Typography sx={{ fontSize: 14, color: '#1f2937', fontWeight: 500 }}>
                                {lead.company || '-'}
                              </Typography>
                            </Box>
                          </Box>
                          {lead.notes && (
                            <Box sx={{
                              p: 1.5,
                              mt: 0.5,
                              background: 'rgba(0,0,0,0.02)',
                              borderRadius: 1.5,
                              border: '1px solid rgba(0,0,0,0.05)'
                            }}>
                              <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 0.5 }}>
                                Notas
                              </Typography>
                              <Typography sx={{ fontSize: 13, color: '#6b7280', whiteSpace: 'pre-wrap' }}>
                                {lead.notes}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    <Divider />

                    {/* Tags Section */}
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalOfferIcon sx={{ fontSize: 16, color: '#ec4899' }} />
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>Tags</Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => setShowTagSelector(!showTagSelector)}
                          sx={{ p: 0.5 }}
                        >
                          <AddIcon sx={{ fontSize: 16, color: '#6b7280' }} />
                        </IconButton>
                      </Box>

                      {/* Current Tags */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: showTagSelector ? 1 : 0 }}>
                        {lead.tags && lead.tags.length > 0 ? (
                          lead.tags.map((tag) => (
                            <Chip
                              key={tag.id}
                              label={tag.name}
                              size="small"
                              onDelete={() => handleRemoveTag(tag.id)}
                              sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 500,
                                background: `${tag.color}20`,
                                color: tag.color,
                                border: `1px solid ${tag.color}40`,
                                '& .MuiChip-deleteIcon': {
                                  fontSize: 14,
                                  color: tag.color,
                                  '&:hover': { color: tag.color }
                                }
                              }}
                            />
                          ))
                        ) : (
                          <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>
                            Nenhuma tag
                          </Typography>
                        )}
                      </Box>

                      {/* Tag Selector */}
                      {showTagSelector && (
                        <Box sx={{
                          p: 1,
                          borderRadius: 1.5,
                          background: 'rgba(0,0,0,0.02)',
                          border: '1px solid rgba(0,0,0,0.05)'
                        }}>
                          <Typography sx={{ fontSize: 11, color: '#6b7280', mb: 0.5 }}>
                            Adicionar tag:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {availableTags
                              .filter(tag => !lead.tags?.some(t => t.id === tag.id))
                              .map((tag) => (
                                <Chip
                                  key={tag.id}
                                  label={tag.name}
                                  size="small"
                                  onClick={() => handleAddTag(tag.id)}
                                  disabled={savingTag}
                                  sx={{
                                    height: 22,
                                    fontSize: 11,
                                    cursor: 'pointer',
                                    background: 'white',
                                    border: `1px solid ${tag.color}`,
                                    color: tag.color,
                                    '&:hover': {
                                      background: `${tag.color}15`
                                    }
                                  }}
                                />
                              ))}
                            {availableTags.filter(tag => !lead.tags?.some(t => t.id === tag.id)).length === 0 && !showCreateTag && (
                              <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>
                                Todas as tags já foram adicionadas
                              </Typography>
                            )}
                          </Box>

                          {/* Create New Tag */}
                          {!showCreateTag ? (
                            <Button
                              size="small"
                              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                              onClick={() => setShowCreateTag(true)}
                              sx={{
                                textTransform: 'none',
                                fontSize: 11,
                                color: '#6b7280',
                                '&:hover': { background: 'rgba(0,0,0,0.05)' }
                              }}
                            >
                              Criar nova tag
                            </Button>
                          ) : (
                            <Box sx={{
                              p: 1,
                              borderRadius: 1,
                              background: 'white',
                              border: '1px solid rgba(0,0,0,0.1)'
                            }}>
                              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                <TextField
                                  size="small"
                                  placeholder="Nome da tag"
                                  value={newTagName}
                                  onChange={(e) => setNewTagName(e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && handleCreateTag()}
                                  sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: 12,
                                      height: 32
                                    }
                                  }}
                                />
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1,
                                    background: newTagColor,
                                    cursor: 'pointer',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    border: '2px solid white',
                                    boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                                  }}
                                >
                                  <input
                                    type="color"
                                    value={newTagColor}
                                    onChange={(e) => setNewTagColor(e.target.value)}
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      opacity: 0,
                                      cursor: 'pointer'
                                    }}
                                  />
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setShowCreateTag(false);
                                    setNewTagName('');
                                  }}
                                  sx={{ fontSize: 11, textTransform: 'none', minWidth: 0, px: 1 }}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={handleCreateTag}
                                  disabled={!newTagName.trim() || creatingTag}
                                  sx={{
                                    fontSize: 11,
                                    textTransform: 'none',
                                    minWidth: 0,
                                    px: 1.5,
                                    background: newTagColor,
                                    '&:hover': { background: newTagColor, filter: 'brightness(0.9)' }
                                  }}
                                >
                                  {creatingTag ? <CircularProgress size={14} color="inherit" /> : 'Criar'}
                                </Button>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>

                    <Divider />

                    {/* Histórico Comercial Section */}
                    {(lead.compras_count || lead.ticket_medio || lead.gasto_total) && (
                      <>
                        <Box sx={{ px: 2, py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <ShoppingCartIcon sx={{ fontSize: 16, color: '#10b981' }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                              Histórico Comercial
                            </Typography>
                          </Box>

                          <Box sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, rgba(16,185,129,0.1), rgba(16,185,129,0.05))',
                            border: '1px solid rgba(16,185,129,0.2)'
                          }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                              <Box>
                                <Typography sx={{ fontSize: 10, color: '#6b7280' }}>Compras</Typography>
                                <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                                  {lead.compras_count || 0}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: 10, color: '#6b7280' }}>Ticket Médio</Typography>
                                <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                                  {lead.ticket_medio ? formatCurrency(Number(lead.ticket_medio)) : '-'}
                                </Typography>
                              </Box>
                              <Box sx={{ gridColumn: '1 / -1' }}>
                                <Typography sx={{ fontSize: 10, color: '#6b7280' }}>Total Gasto</Typography>
                                <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#10b981' }}>
                                  {lead.gasto_total ? formatCurrency(Number(lead.gasto_total)) : '-'}
                                </Typography>
                              </Box>
                              {lead.ultima_compra && (
                                <Box sx={{ gridColumn: '1 / -1' }}>
                                  <Typography sx={{ fontSize: 10, color: '#6b7280' }}>Última Compra</Typography>
                                  <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#1f2937' }}>
                                    {formatTimeAgo(lead.ultima_compra)}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                        <Divider />
                      </>
                    )}

                    {/* Quick Actions Section */}
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TaskAltIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                          Ações Rápidas
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EventIcon sx={{ fontSize: 14 }} />}
                          onClick={() => handleQuickAction('follow-up')}
                          sx={{
                            flex: 1,
                            textTransform: 'none',
                            fontSize: 11,
                            py: 0.75,
                            borderColor: '#6366f1',
                            color: '#6366f1',
                            '&:hover': { background: 'rgba(99,102,241,0.1)' }
                          }}
                        >
                          Follow-up
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DescriptionIcon sx={{ fontSize: 14 }} />}
                          onClick={() => handleQuickAction('proposal')}
                          sx={{
                            flex: 1,
                            textTransform: 'none',
                            fontSize: 11,
                            py: 0.75,
                            borderColor: '#10b981',
                            color: '#10b981',
                            '&:hover': { background: 'rgba(16,185,129,0.1)' }
                          }}
                        >
                          Proposta
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PhoneIcon sx={{ fontSize: 14 }} />}
                          onClick={() => handleQuickAction('call')}
                          sx={{
                            flex: 1,
                            textTransform: 'none',
                            fontSize: 11,
                            py: 0.75,
                            borderColor: '#f59e0b',
                            color: '#f59e0b',
                            '&:hover': { background: 'rgba(245,158,11,0.1)' }
                          }}
                        >
                          Ligar
                        </Button>
                      </Box>
                    </Box>

                    <Divider />

                    {/* Quick Notes Section */}
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <NoteAddIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                          Notas Rápidas
                        </Typography>
                      </Box>

                      {/* Add Note Input */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Adicionar nota..."
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: 12,
                              borderRadius: 1.5
                            }
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={handleAddNote}
                          disabled={!newNote.trim() || savingNote}
                          sx={{
                            background: newNote.trim() ? 'linear-gradient(135deg, #8b5cf6, #6366f1)' : 'rgba(0,0,0,0.05)',
                            color: newNote.trim() ? 'white' : '#9ca3af',
                            '&:hover': {
                              background: newNote.trim() ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : 'rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          {savingNote ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Box>

                      {/* Notes List */}
                      {loadingLeadInfo ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                          <CircularProgress size={16} />
                        </Box>
                      ) : leadNotes.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {leadNotes.map((note) => (
                            <Box
                              key={note.id}
                              sx={{
                                p: 1,
                                borderRadius: 1.5,
                                background: 'rgba(139,92,246,0.05)',
                                border: '1px solid rgba(139,92,246,0.15)',
                                position: 'relative',
                                '&:hover .delete-note-btn': {
                                  opacity: 1
                                }
                              }}
                            >
                              <IconButton
                                className="delete-note-btn"
                                size="small"
                                onClick={() => handleDeleteNote(note.id)}
                                disabled={deletingNoteId === note.id}
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  p: 0.25,
                                  opacity: 0,
                                  transition: 'opacity 0.2s',
                                  color: '#ef4444',
                                  '&:hover': { background: 'rgba(239,68,68,0.1)' }
                                }}
                              >
                                {deletingNoteId === note.id ? (
                                  <CircularProgress size={12} sx={{ color: '#ef4444' }} />
                                ) : (
                                  <CancelIcon sx={{ fontSize: 14 }} />
                                )}
                              </IconButton>
                              <Typography sx={{ fontSize: 12, color: '#1f2937', whiteSpace: 'pre-wrap', pr: 2 }}>
                                {note.content}
                              </Typography>
                              <Typography sx={{ fontSize: 10, color: '#9ca3af', mt: 0.5 }}>
                                {note.created_by_name || 'Você'} • {formatTimeAgo(note.created_at)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', py: 1 }}>
                          Nenhuma nota cadastrada
                        </Typography>
                      )}
                    </Box>

                    <Divider />

                    {/* Tasks Section */}
                    <Accordion
                      expanded={expandedSection === 'tasks'}
                      onChange={(_, exp) => setExpandedSection(exp ? 'tasks' : false)}
                      disableGutters
                      elevation={0}
                      sx={{
                        background: 'transparent',
                        '&:before': { display: 'none' }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          px: 2,
                          minHeight: 48,
                          '& .MuiAccordionSummary-content': { my: 0 }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TaskAltIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                            Tarefas
                          </Typography>
                          {tasks.filter(t => t.status !== 'completed').length > 0 && (
                            <Chip
                              label={tasks.filter(t => t.status !== 'completed').length}
                              size="small"
                              sx={{
                                height: 18,
                                minWidth: 18,
                                fontSize: 10,
                                background: 'rgba(245,158,11,0.15)',
                                color: '#f59e0b',
                                ml: 'auto'
                              }}
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, pt: 0 }}>
                        {loadingTasks ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={20} />
                          </Box>
                        ) : (
                          <>
                            {/* Task List */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                              {tasks.length === 0 ? (
                                <Typography variant="body2" sx={{ color: '#9ca3af', textAlign: 'center', py: 2 }}>
                                  Nenhuma tarefa cadastrada
                                </Typography>
                              ) : (
                                tasks.map((task) => (
                                  <Box
                                    key={task.id}
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 1.5,
                                      background: task.status === 'completed' ? 'rgba(34,197,94,0.08)' : 'rgba(0,0,0,0.02)',
                                      border: task.status === 'completed' ? '1px solid rgba(34,197,94,0.2)' : '1px solid rgba(0,0,0,0.05)',
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: 1
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => handleToggleTaskStatus(task.id, task.status)}
                                      sx={{
                                        p: 0.25,
                                        color: task.status === 'completed' ? '#22c55e' : '#d1d5db',
                                        '&:hover': { color: task.status === 'completed' ? '#16a34a' : '#22c55e' }
                                      }}
                                    >
                                      <CheckCircleIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          fontWeight: 500,
                                          color: task.status === 'completed' ? '#9ca3af' : '#1f2937',
                                          textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {task.title}
                                      </Typography>
                                      {task.scheduled_at && (
                                        <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.25 }}>
                                          {format(new Date(task.scheduled_at), "dd/MM 'às' HH:mm", { locale: ptBR })}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box
                                        sx={{
                                          width: 6,
                                          height: 6,
                                          borderRadius: '50%',
                                          background: task.priority === 'urgent' ? '#ef4444' :
                                                     task.priority === 'high' ? '#f59e0b' :
                                                     task.priority === 'medium' ? '#3b82f6' : '#9ca3af'
                                        }}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteTask(task.id)}
                                        sx={{ p: 0.25, color: '#d1d5db', '&:hover': { color: '#ef4444' } }}
                                      >
                                        <CancelIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                ))
                              )}
                            </Box>

                            <Button
                              size="small"
                              fullWidth
                              onClick={() => setTaskModalOpen(true)}
                              startIcon={<TaskAltIcon />}
                              sx={{
                                textTransform: 'none',
                                color: '#f59e0b',
                                borderColor: '#f59e0b',
                                '&:hover': { background: 'rgba(245,158,11,0.1)' }
                              }}
                              variant="outlined"
                            >
                              Nova Tarefa
                            </Button>
                          </>
                        )}
                      </AccordionDetails>
                    </Accordion>

                    <Divider />

                    {/* AI Agent Section */}
                    <Accordion
                      expanded={expandedSection === 'ai'}
                      onChange={(_, exp) => setExpandedSection(exp ? 'ai' : false)}
                      disableGutters
                      elevation={0}
                      sx={{
                        background: 'transparent',
                        '&:before': { display: 'none' }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          px: 2,
                          minHeight: 48,
                          '& .MuiAccordionSummary-content': { my: 0 }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SmartToyIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                            Agente IA
                          </Typography>
                          {conversationAgent && (
                            <Chip
                              label={conversationAgent.is_human_handling ? 'Humano' : conversationAgent.is_active ? 'Ativo' : 'Inativo'}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                background: conversationAgent.is_human_handling
                                  ? 'rgba(245,158,11,0.15)'
                                  : conversationAgent.is_active
                                    ? 'rgba(34,197,94,0.15)'
                                    : 'rgba(156,163,175,0.15)',
                                color: conversationAgent.is_human_handling
                                  ? '#f59e0b'
                                  : conversationAgent.is_active
                                    ? '#22c55e'
                                    : '#9ca3af',
                                ml: 'auto'
                              }}
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, pt: 0 }}>
                        {loadingAgent ? (
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : conversationAgent ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Status Card */}
                            <Box sx={{
                              p: 2,
                              borderRadius: 2,
                              background: conversationAgent.is_human_handling
                                ? 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(245,158,11,0.05))'
                                : conversationAgent.is_active
                                  ? 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))'
                                  : 'linear-gradient(135deg, rgba(156,163,175,0.15), rgba(156,163,175,0.05))',
                              border: conversationAgent.is_human_handling
                                ? '2px solid rgba(245,158,11,0.4)'
                                : conversationAgent.is_active
                                  ? '2px solid rgba(34,197,94,0.4)'
                                  : '2px solid rgba(156,163,175,0.4)'
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                {conversationAgent.is_human_handling ? (
                                  <SupportAgentIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                                ) : (
                                  <SmartToyIcon sx={{ fontSize: 20, color: conversationAgent.is_active ? '#22c55e' : '#9ca3af' }} />
                                )}
                                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                  {conversationAgent.is_human_handling ? 'Atendimento Humano' : conversationAgent.agent_name}
                                </Typography>
                              </Box>
                              {conversationAgent.is_human_handling && conversationAgent.handoff_by_name && (
                                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                                  Atendido por: {conversationAgent.handoff_by_name}
                                </Typography>
                              )}
                              {conversationAgent.is_human_handling && conversationAgent.handoff_reason_display && (
                                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                                  Motivo: {conversationAgent.handoff_reason_display}
                                </Typography>
                              )}
                            </Box>

                            {/* Action Buttons */}
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {conversationAgent.is_human_handling ? (
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  startIcon={togglingAgent ? <CircularProgress size={16} /> : <SwapHorizIcon />}
                                  onClick={handleReturnToBot}
                                  disabled={togglingAgent}
                                  sx={{
                                    borderColor: '#06b6d4',
                                    color: '#06b6d4',
                                    '&:hover': { borderColor: '#0891b2', background: 'rgba(6,182,212,0.1)' }
                                  }}
                                >
                                  Devolver ao Bot
                                </Button>
                              ) : conversationAgent.is_active ? (
                                <>
                                  <Button
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    startIcon={togglingAgent ? <CircularProgress size={16} /> : <SupportAgentIcon />}
                                    onClick={() => setHandoffModalOpen(true)}
                                    disabled={togglingAgent}
                                    sx={{
                                      borderColor: '#f59e0b',
                                      color: '#f59e0b',
                                      '&:hover': { borderColor: '#d97706', background: 'rgba(245,158,11,0.1)' }
                                    }}
                                  >
                                    Assumir
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleDeactivateAgent}
                                    disabled={togglingAgent}
                                    sx={{
                                      minWidth: 40,
                                      borderColor: '#ef4444',
                                      color: '#ef4444',
                                      '&:hover': { borderColor: '#dc2626', background: 'rgba(239,68,68,0.1)' }
                                    }}
                                  >
                                    <StopIcon fontSize="small" />
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  startIcon={togglingAgent ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                                  onClick={handleReactivateAgent}
                                  disabled={togglingAgent}
                                  sx={{
                                    borderColor: '#22c55e',
                                    color: '#22c55e',
                                    '&:hover': { borderColor: '#16a34a', background: 'rgba(34,197,94,0.1)' }
                                  }}
                                >
                                  Reativar Bot
                                </Button>
                              )}
                            </Box>

                            {/* Switch Agent Option */}
                            {!conversationAgent.is_human_handling && availableAgents.length > 1 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 1 }}>
                                  Trocar agente:
                                </Typography>
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value=""
                                    displayEmpty
                                    onChange={(e) => e.target.value && handleSwitchAgent(Number(e.target.value))}
                                    disabled={togglingAgent}
                                    sx={{ fontSize: 13 }}
                                  >
                                    <MenuItem value="" disabled>
                                      <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                                        Selecionar outro agente...
                                      </Typography>
                                    </MenuItem>
                                    {availableAgents
                                      .filter(a => a.id !== conversationAgent.agent)
                                      .map((agent) => (
                                        <MenuItem key={agent.id} value={agent.id}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SmartToyIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                            {agent.name}
                                          </Box>
                                        </MenuItem>
                                      ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              Nenhum agente ativo nesta conversa
                            </Typography>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Selecionar Agente</InputLabel>
                              <Select
                                value={selectedAgentId || ''}
                                label="Selecionar Agente"
                                onChange={(e) => setSelectedAgentId(e.target.value as number)}
                              >
                                {availableAgents.map((agent) => (
                                  <MenuItem key={agent.id} value={agent.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <SmartToyIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                      {agent.name}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Button
                              fullWidth
                              variant="contained"
                              size="small"
                              startIcon={togglingAgent ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                              onClick={handleActivateAgent}
                              disabled={togglingAgent || !selectedAgentId}
                              sx={{
                                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                                '&:hover': { background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)' }
                              }}
                            >
                              Ativar Agente
                            </Button>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>

                    {/* Handoff Modal */}
                    <Dialog
                      open={handoffModalOpen}
                      onClose={() => setHandoffModalOpen(false)}
                      maxWidth="xs"
                      fullWidth
                      PaperProps={{
                        sx: {
                          borderRadius: 3,
                          ...glassStyle
                        }
                      }}
                    >
                      <DialogTitle sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SupportAgentIcon sx={{ color: '#f59e0b' }} />
                          <Typography variant="h6">Assumir Atendimento</Typography>
                        </Box>
                      </DialogTitle>
                      <DialogContent>
                        <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                          Você está assumindo o atendimento desta conversa. O bot será desativado.
                        </Typography>
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>Motivo</Typography>
                          <RadioGroup
                            value={handoffReason}
                            onChange={(e) => setHandoffReason(e.target.value)}
                          >
                            <FormControlLabel value="user_request" control={<Radio size="small" />} label="Solicitação do Usuário" />
                            <FormControlLabel value="complex_issue" control={<Radio size="small" />} label="Assunto Complexo" />
                            <FormControlLabel value="agent_limit" control={<Radio size="small" />} label="Limite do Agente" />
                            <FormControlLabel value="error" control={<Radio size="small" />} label="Erro do Agente" />
                            <FormControlLabel value="manual" control={<Radio size="small" />} label="Intervenção Manual" />
                          </RadioGroup>
                        </FormControl>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Observações (opcional)"
                          value={handoffNotes}
                          onChange={(e) => setHandoffNotes(e.target.value)}
                          placeholder="Adicione observações sobre o motivo do handoff..."
                        />
                      </DialogContent>
                      <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setHandoffModalOpen(false)} color="inherit">
                          Cancelar
                        </Button>
                        <Button
                          onClick={handleHandoff}
                          variant="contained"
                          disabled={togglingAgent}
                          sx={{
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)' }
                          }}
                        >
                          {togglingAgent ? <CircularProgress size={20} color="inherit" /> : 'Assumir'}
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </>
                )}
              </Box>
            </Box>
          )}
      </Box>

      {/* Activity Form Modal */}
      <ActivityForm
        open={taskModalOpen}
        onClose={() => {
          setTaskModalOpen(false);
          setEditingTask(undefined);
        }}
        onSave={handleSaveTask}
        activity={editingTask}
        leads={lead ? [{
          id: lead.id,
          name: lead.name,
          email: lead.email || undefined,
          phone: lead.phone || undefined,
          company: lead.company || undefined,
          status: lead.status,
          source: lead.source || undefined,
          notes: lead.notes || undefined,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }] : []}
        deals={opportunity ? [{
          id: opportunity.id,
          name: opportunity.name || opportunity.pipeline_name || `Negociação ${lead?.name || ''}`,
          lead: opportunity.lead,
          status: opportunity.status
        }] : []}
        users={companyUsers}
        isSubmitting={savingTask}
        defaultDealId={opportunity?.id}
        defaultLeadId={lead?.id}
        currentUserId={user?.id}
      />

      {/* Lead Edit Modal */}
      {lead && (
        <LeadEditModal
          open={leadEditModalOpen}
          onClose={() => setLeadEditModalOpen(false)}
          lead={lead}
          onSave={handleLeadUpdated}
        />
      )}
    </Box>
  );
};

export default Messaging;
